// i18n area 'tasks' (TVirt task #127 layout, adopted for TVStor M1): section
// tasks — the monitor list, the detail dialog with its structured log, the
// cancel/resume/abort actions and the navbar dropdown.

export const en = {
  tasks: {
    title: 'Tasks',
    searchPlaceholder: 'Search by type or target',
    type: 'Type',
    target: 'Target',
    phaseLabel: 'Phase',
    progress: 'Progress',
    started: 'Started',
    finished: 'Finished',
    node: 'Node',
    empty: 'No tasks',
    loadError: 'Could not load the tasks — is the TVStor backend reachable?',

    // #181 ondata 5: cursor pagination. Older pages are a browsing gesture, so
    // while they are shown the 5s live refresh pauses (a head-only refresh
    // would silently mix a fresh first page with stale history).
    loadMore: 'Load older tasks',
    refreshPaused: 'Live refresh is paused while older pages are shown — go back to the newest page to resume.',
    backToNewest: 'Back to the newest page',
    limboHint: "Node '{node}' unreachable since {time} — the task holds its target's write lease until the node returns or is attested down",
    phase: {
      queued: 'Queued',
      running: 'Running',
      succeeded: 'Succeeded',
      failed: 'Failed',
      cancelled: 'Cancelled',
      aborted: 'Aborted'
    },
    dropdown: {
      tooltip: 'Running tasks',
      title: 'Running tasks',
      empty: 'No tasks in progress',
      viewAll: 'View all tasks'
    },
    detail: {
      open: 'Details',
      scope: 'Scope',
      message: 'Message',
      params: 'Parameters',
      result: 'Result',
      errorTitle: 'Error',
      steps: 'Steps',
      failedAt: 'failed at',

      // TASK-CANCELSEM (ratifica 2026-08-12 §4): a family the operator stopped
      // closes `cancelled` and can still name a step that failed on its way out.
      // The wording must not let that step read as the reason the family ended.
      failedWhileStopping: 'failed while stopping at',
      failedOnCancelHint: 'The task was cancelled on request; this step failed while stopping.',
      superseded: 'superseded',
      step: 'Step',
      parent: 'Parent task',
      failedStep: 'Failed step',
      log: 'Log',
      logEmpty: 'No log entries yet',

      // #181 ondata 5 — the trailer frame (`stream {complete, reason, lines}`,
      // tasks.yaml: the stream ALWAYS ends with it; `complete` is ternary).
      // The reason is backend prose, rendered verbatim: never re-worded here.
      logSnapshot: 'The task is still running — this is a snapshot, refreshed every few seconds.',
      logComplete: 'End of log — {lines} line(s), complete.',
      logIncomplete: 'Log INCOMPLETE after {lines} line(s): {reason}',
      logNotAsserted: 'Completeness not assertable after {lines} line(s): {reason} — reopen to re-check.',
      logCut: 'The log stream ended WITHOUT its trailer: the connection itself was cut — reload the detail.',
      close: 'Close'
    },
    actions: {
      cancel: 'Cancel',
      resume: 'Retry from the failed step',
      abort: 'Abort (undo completed steps)',
      refused: 'The action was refused',

      // #181 ondata 5 — the AbortResult (tasks.yaml AbortResult): undo actions
      // run in reverse where declared, honest where they do not exist.
      abortDoneTitle: 'Abort completed',
      abortUndone: 'Steps undone (declared undo actions, run in reverse):',
      abortReported: 'Completed steps WITHOUT a declared undo — merely reported, nothing was rolled back:',
      abortNothing: 'No completed steps to undo: the family closed aborted with nothing to roll back.'
    }
  }
}

export const it: typeof en = {
  tasks: {
    title: 'Task',
    searchPlaceholder: 'Cerca per tipo o target',
    type: 'Tipo',
    target: 'Target',
    phaseLabel: 'Fase',
    progress: 'Avanzamento',
    started: 'Iniziato',
    finished: 'Terminato',
    node: 'Nodo',
    empty: 'Nessun task',
    loadError: 'Impossibile caricare i task — il backend TVStor è raggiungibile?',
    loadMore: 'Carica task più vecchi',
    refreshPaused: "L'aggiornamento in tempo reale è in pausa finché sono mostrate le pagine più vecchie — torna alla prima pagina per riprenderlo.",
    backToNewest: 'Torna alla pagina più recente',
    limboHint: "Nodo '{node}' irraggiungibile dalle {time} — il task trattiene il write lease sul suo target finché il nodo non torna o non viene attestato spento",
    phase: {
      queued: 'In coda',
      running: 'In esecuzione',
      succeeded: 'Completato',
      failed: 'Fallito',
      cancelled: 'Annullato',
      aborted: 'Interrotto'
    },
    dropdown: {
      tooltip: 'Task in corso',
      title: 'Task in corso',
      empty: 'Nessun task in corso',
      viewAll: 'Vedi tutti i task'
    },
    detail: {
      open: 'Dettagli',
      scope: 'Ambito',
      message: 'Messaggio',
      params: 'Parametri',
      result: 'Risultato',
      errorTitle: 'Errore',
      steps: 'Passi',
      failedAt: 'fallito a',
      failedWhileStopping: 'fallito arrestandosi a',
      failedOnCancelHint: 'Il task è stato annullato su richiesta; questo passo è fallito mentre si arrestava.',
      superseded: 'superato',
      step: 'Passo',
      parent: 'Task padre',
      failedStep: 'Passo fallito',
      log: 'Log',
      logEmpty: 'Ancora nessuna riga di log',
      logSnapshot: 'Il task è ancora in esecuzione — questa è un\'istantanea, aggiornata ogni pochi secondi.',
      logComplete: 'Fine del log — {lines} righe, completo.',
      logIncomplete: 'Log INCOMPLETO dopo {lines} righe: {reason}',
      logNotAsserted: 'Completezza non attestabile dopo {lines} righe: {reason} — riapri per ricontrollare.',
      logCut: 'Lo stream del log è finito SENZA il suo trailer: la connessione stessa si è interrotta — ricarica il dettaglio.',
      close: 'Chiudi'
    },
    actions: {
      cancel: 'Cancella',
      resume: 'Riprova dal passo fallito',
      abort: 'Annulla tutto (undo dei passi completati)',
      refused: "L'azione è stata rifiutata",
      abortDoneTitle: 'Annullamento completato',
      abortUndone: 'Passi annullati (undo dichiarati, eseguiti in ordine inverso):',
      abortReported: 'Passi completati SENZA undo dichiarato — solo riportati, nulla è stato annullato:',
      abortNothing: 'Nessun passo completato da annullare: la famiglia si è chiusa aborted senza nulla da disfare.'
    }
  }
}
